import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  FiX,
  FiUpload,
  FiFileText,
  FiPaperclip,
  FiSend,
  FiAlertCircle,
} from "react-icons/fi";
import "./styles/JobModal.css";

const ApplyModal = ({ open, job, onClose, onSubmit }) => {
  const { user } = useAuth();

  const [resumeFile, setResumeFile] = useState(null);
  const [documentsFiles, setDocumentsFiles] = useState([]);
  const [coverLetter, setCoverLetter] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const userName = useMemo(() => {
    const n = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    return n || "Candidate";
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!resumeFile) {
      setError("Resume file is required.");
      return;
    }

    setError("");
    setSending(true);

    try {
      const fd = new FormData();
      fd.append("resume", resumeFile);
      fd.append("coverLetter", coverLetter || "");
      documentsFiles.forEach((f) => fd.append("documents", f));

      await onSubmit(fd);
      onClose();
    } catch (err) {
      const backendMessage = err.response?.data?.message;
      setError(backendMessage || "Failed to submit application. Please try again later.");
      setSending(false);
    }
  };

  if (!open || !job) return null;

  return (
    <div className="jb-modal-overlay" onMouseDown={onClose}>
      <div className="jb-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="jb-modal-header">
          <div className="jb-modal-titlewrap">
            <h3 className="jb-modal-title">Apply for {job.title}</h3>
            <p className="jb-modal-subtitle">
              Submitting as <span className="jb-modal-strong">{userName}</span>
            </p>
          </div>

          <button className="jb-icon-btn" type="button" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <form className="jb-modal-body" onSubmit={handleSubmit}>
          {error && (
            <div className="jb-alert jb-alert--error">
              <FiAlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div className="jb-field">
            <label className="jb-label">
              <FiFileText className="jb-label-ic" />
              Resume <span className="jb-required">*</span>
            </label>

            <label className="jb-file">
              <FiUpload />
              <span>{resumeFile ? resumeFile.name : "Upload resume (PDF/DOC)"}</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
              />
            </label>

            {resumeFile && (
              <div className="jb-filehint">
                Selected: <span className="jb-muted">{resumeFile.name}</span>
              </div>
            )}
          </div>

          <div className="jb-field">
            <label className="jb-label">
              <FiPaperclip className="jb-label-ic" />
              Additional documents (optional)
            </label>

            <label className="jb-file jb-file--secondary">
              <FiUpload />
              <span>
                {documentsFiles.length > 0
                  ? `${documentsFiles.length} file(s) selected`
                  : "Upload supporting documents"}
              </span>
              <input
                type="file"
                multiple
                onChange={(e) => setDocumentsFiles(Array.from(e.target.files || []))}
              />
            </label>

            {documentsFiles.length > 0 && (
              <ul className="jb-filelist">
                {documentsFiles.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="jb-fileitem">
                    <FiPaperclip />
                    <span>{f.name}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="jb-field">
            <label className="jb-label">Cover letter (optional)</label>
            <textarea
              className="jb-textarea"
              rows={5}
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              placeholder="Write a short note to the employer..."
            />
          </div>

          <div className="jb-modal-footer">
            <button type="button" className="jb-btn jb-btn--ghost" onClick={onClose} disabled={sending}>
              Cancel
            </button>

            <button type="submit" className="jb-btn jb-btn--primary" disabled={sending}>
              <FiSend style={{ marginRight: 8 }} />
              {sending ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplyModal;
